<template>
  <CContainer v-if="userReady" class="px-4 px-lg-5 mt-5">
    <div class="border-top pt-4 mb-4">
      <h4 class="text-end fw-bold">Register Candidate</h4>
    </div>

    <!-- Loading Spinner -->
    <div v-if="loading" class="text-center py-5">
      <p>Loading registration status...</p>
    </div>

    <!-- Card Grid -->
    <CRow v-else class="gx-3 gy-4 justify-content-center">
      <CCol v-if="activeCardItems.length === 0" class="text-center">
        <p class="text-muted">No registration is open at this time.</p>
      </CCol>

      <CCol
          v-for="(item, index) in activeCardItems"
          :key="index"
          :xs="12"
          :sm="6"
          :md="4"
          :lg="3"
      >
        <div
            class="card-link"
            @click="onCardClick(item)"
            :style="{ pointerEvents: loading ? 'none' : 'auto' }"
        >
          <CCard
              class="square-card card-hover border-0 shadow-sm"
              :class="{ 'card-disabled': isRegistered(item) }"
          >
            <CCardBody
                class="position-relative h-100 text-center p-4 d-flex flex-column justify-content-center"
            >
              <div>
                <h5 class="fw-bold mb-2">{{ item.title }}</h5>
                <p class="mb-3" style="white-space: pre-line">
                  {{ item.subtitle }}
                </p>
                <p v-if="isRegistered(item)" class="text-danger small fw-bold mb-0">
                  Already registered as: <br />
                  <span>{{ getRegisteredType(item) }}</span> <br />
                  <span>({{ getRegisteredStatus(item) }})</span>
                </p>
              </div>
              <CIcon
                  :name="item.iconName"
                  class="icon-bottom-right"
                  width="45"
                  height="45"
              />
            </CCardBody>
          </CCard>
        </div>
      </CCol>
    </CRow>
  </CContainer>

  <div v-else class="text-center py-5">Loading user...</div>
</template>

<script>
import {
  CCard,
  CCardBody,
  CRow,
  CCol,
  CContainer,
} from '@coreui/vue';
import CIcon from '@coreui/icons-vue';
import api from '@/service/api.js';
import moment from 'moment-timezone';
import Swal from 'sweetalert2';
import { mapGetters } from 'vuex';
import '@/assets/css/card.css';

export default {
  name: 'CardGridFinal',
  components: { CCard, CCardBody, CRow, CCol, CContainer, CIcon },
  data() {
    return {
      setters: [],
      candidateData: null,
      registeredMap: {},
      loading: true,
      cardItems: [
        {
          title: 'President of the Student Union Candidate Register',
          subtitle: '',
          iconName: 'cil-pen-alt',
          route: '/pdpa/president',
          eventId: '68624a7c09800f23242c3da5',
          typeId: '686bad23266df45e88c9ca5c',
        },
        {
          title: 'Student Council Member (School of) Candidate Register',
          subtitle: '',
          iconName: 'cil-pen-alt',
          route: '/pdpa/school',
          eventId: '68624a7c09800f23242c3da5',
          typeId: '686bad33266df45e88c9ca5f',
        },
        {
          title: 'Student Council Member (Normal) Candidate Register',
          subtitle: '',
          iconName: 'cil-pen-alt',
          route: '/pdpa/normal',
          eventId: '68624a7c09800f23242c3da5',
          typeId: '686bacfd266df45e88c9ca59',
        },
      ],
    };
  },
  computed: {
    ...mapGetters({ user: 'auth/user' }),
    userReady() {
      return this.user && this.user._id;
    },
    activeCardItems() {
      const now = moment().tz('Asia/Bangkok');
      console.log("📍 [activeCardItems] NOW:", now.format());

      if (this.candidateData?.type) {
        const typeId = this.idOf(this.candidateData.type);
        const eventId = this.idOf(this.candidateData.event);

        console.log("✅ User is registered. Show only matching card:", {
          typeId,
          eventId,
        });

        return this.cardItems.filter(
            (item) =>
                this.idOf(item.typeId) === typeId &&
                this.idOf(item.eventId) === eventId
        );
      }

      console.log("❌ User is NOT registered. Checking setter time windows...");

      return this.cardItems.filter((item) => {
        const s = this.setters.find(
            (s) =>
                this.idOf(s.title) === this.idOf(item.eventId) &&
                this.idOf(s.type) === this.idOf(item.typeId)
        );

        if (!s) {
          console.log("⛔ No setter match found for card:", item.title);
          return false;
        }
        const start = moment.utc(s.startDate).tz('Asia/Bangkok');
        const end = moment.utc(s.endDate).tz('Asia/Bangkok');
        const inRange = now.isBetween(start, end, null, '[]');

        console.log(`🗓️ Time check for "${item.title}":`, {
          start: start.format(),
          end: end.format(),
          now: now.format(),
          isActive: inRange
        });

        return inRange;
      });
    },
  },
  async mounted() {
    await this.loadSetters();
  },
  watch: {
    user: {
      immediate: true,
      handler(newUser) {
        if (newUser && newUser._id) {
          this.checkIfRegistered();
        } else {
          this.loading = false;
        }
      },
    },
  },
  methods: {
    async loadSetters() {
      try {
        const { data } = await api.setter('get');
        this.setters = data.data || [];
        console.log("📦 Setters loaded:", this.setters);
      } catch (err) {
        console.error(err);
        Swal.fire('Error', 'Could not load registration windows.', 'error');
      }
    },
    async checkIfRegistered() {
      try {
        const res = await api.candidate('checkRegistered', {
          accountID: this.user._id,
        });

        const candidate = res.data?.data;
        console.log("🎯 checkIfRegistered result:", candidate);

        if (!candidate) {
          console.log("❌ No candidate found — user not registered.");
          return;
        }

        this.candidateData = candidate;

        const typeId = this.idOf(candidate?.type);
        const eventId = this.idOf(candidate?.event);
        const key = `${eventId}-${typeId}`;

        this.registeredMap[key] = {
          typeName: candidate?.type?.type_name || '',
          statusName: candidate?.status?.status_name || '',
        };
      } catch (err) {
        console.error("🚨 Error in checkIfRegistered:", err);
        Swal.fire('Error', 'Could not check registration status.', 'error');
      } finally {
        this.loading = false;
      }
    },
    idOf(field) {
      return typeof field === 'object' && field !== null
          ? String(field._id || field.id)
          : String(field);
    },
    isRegistered(item) {
      const key = `${this.idOf(item.eventId)}-${this.idOf(item.typeId)}`;
      return !!this.registeredMap[key];
    },
    getRegisteredType(item) {
      const key = `${this.idOf(item.eventId)}-${this.idOf(item.typeId)}`;
      return this.registeredMap[key]?.typeName || '';
    },
    getRegisteredStatus(item) {
      const key = `${this.idOf(item.eventId)}-${this.idOf(item.typeId)}`;
      return this.registeredMap[key]?.statusName || '';
    },
    onCardClick(item) {
      if (this.loading) return;

      if (this.isRegistered(item)) {
        Swal.fire({
          title: 'Already Registered',
          html: `
    <div id="swal-content-inline">
    <div style="font-size: 1.25rem; color: #008000; margin-bottom: 0.5rem;">
        (${this.getRegisteredStatus(item)})
      </div>
      <p style="color: #888; font-size: 1rem; margin-bottom: 0.25rem;">
        You have already registered for
      </p>
      <div style="font-size: 1.25rem; font-weight: 600; color: #222; margin-bottom: 0.25rem;">
        ${this.getRegisteredType(item)}
      </div>
    </div>
  `,
          confirmButtonText: 'OK',
          showCancelButton: false,
          background: '#fff',
          customClass: {
            popup: 'inline-swal-card',
          },
          didOpen: () => {
            const popup = Swal.getPopup();
            if (popup) {
              popup.style.borderRadius = '1rem';
              popup.style.padding = '1.5rem';
              popup.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.15)';
              popup.style.fontFamily = 'Segoe UI, sans-serif';
            }

            const title = popup?.querySelector('.swal2-title');
            if (title) {
              title.style.fontSize = '1.75rem';
              title.style.fontWeight = 'bold';
              title.style.color = '#008000';
              title.style.marginBottom = '1rem';
            }

            const confirmBtn = Swal.getConfirmButton();
            if (confirmBtn) {
              confirmBtn.style.backgroundColor = 'transparent';
              confirmBtn.style.border = '2px solid #d33';
              confirmBtn.style.color = '#d33';
              confirmBtn.style.fontSize = '0.9rem';
              confirmBtn.style.padding = '6px 16px';
              confirmBtn.style.borderRadius = '999px';
              confirmBtn.style.boxShadow = 'none';
              confirmBtn.style.transition = 'all 0.2s ease';

              // ➕ Hover effect
              confirmBtn.onmouseenter = () => {
                confirmBtn.style.backgroundColor = '#d33';
                confirmBtn.style.color = '#fff';
              };
              confirmBtn.onmouseleave = () => {
                confirmBtn.style.backgroundColor = 'transparent';
                confirmBtn.style.color = '#d33';
              };
            }
          }
        });
      } else {
        this.$router.push(item.route);
      }
    }
  },
};
</script>

<style scoped>
.card-disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
</style>
